import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { useLazyCommand, useEvent, useApiErrors } from '@soap/modules';
import { useToast } from '../../ui/use-toast';
import { UserPlus, X } from 'lucide-react';
import { UnarchiveUserDialog } from './UnarchiveUserDialog';

export function InviteUserToOrg(props) {
    
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [userRole, setUserRole] = useState('teacher');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isSubmittingRef = useRef(false);
    const submittedEmailRef = useRef('');
    const [showUnarchiveTeacherDialog, setShowUnarchiveTeacherDialog] = useState(false);
    const [unarchiveTeacherUserId, setUnarchiveTeacherUserId] = useState<string | null>(null);
    
    const [inviteCommand] = useLazyCommand("C236v1_InviteUserToOrganisation");
    const [unarchiveTeacherCommand] = useLazyCommand('C243v1_UnarchiveTeacher');
    const { toast } = useToast();
    const { errors } = useApiErrors();

    const close = useCallback(() => {
        setOpen(false);
        // Reset form
        setEmail('');
        setFirstName('');
        setLastName('');
        setUserRole('teacher');
        setIsSubmitting(false);
    }, []);

    // Listen for successful invitation event
    useEvent({
        eventName: "E201v1_OrgUserInvited",
        onEventReceived: () => {
            if (isSubmittingRef.current) {
                const emailToShow = submittedEmailRef.current;
                // Show toast notification
                toast({
                    title: "Invitation sent",
                    description: `An invitation has been sent to ${emailToShow}.`,
                });
                // Close dialog and reset form
                close();
                isSubmittingRef.current = false;
                submittedEmailRef.current = '';
            }
        }
    });

    // Listen for email reuse requires unarchive event
    useEvent({
        eventName: "E203v1_EmailReuseRequiresUnarchive",
        onEventReceived: (event: any) => {
            if (isSubmittingRef.current) {
                const errorCode = event.e203_ErrorCode;
                if (errorCode === "EmailReuseRequiresUnarchiveTeacher") {
                    // Show unarchive teacher dialog
                    // Convert Guid to string if needed (event may serialize Guid as string)
                    const userId = event.e203_UserId;
                    setUnarchiveTeacherUserId(userId ? (typeof userId === 'string' ? userId : userId.toString()) : null);
                    setShowUnarchiveTeacherDialog(true);
                    setIsSubmitting(false);
                    isSubmittingRef.current = false;
                }
                // Note: Student unarchive is handled in AddStudentPage, EditStudentPage, AssignSubAccountDialog
            }
        }
    });

    // Listen for successful unarchive
    useEvent({
        eventName: "E197v1_OrganisationMembersUpdated",
        onEventReceived: () => {
            if (showUnarchiveTeacherDialog) {
                setShowUnarchiveTeacherDialog(false);
                setUnarchiveTeacherUserId(null);
                close();
            }
        }
    });

    // Reset submitting state when an error occurs
    useEffect(() => {
        if (errors.length > 0 && isSubmittingRef.current) {
            setIsSubmitting(false);
            isSubmittingRef.current = false;
        }
    }, [errors]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        
        setIsSubmitting(true);
        isSubmittingRef.current = true;
        submittedEmailRef.current = email.trim();
        try {
            inviteCommand({
                C236_Email: email.trim(),
                C236_FirstName: firstName.trim(),
                C236_LastName: lastName.trim(),
                C236_RoleKey: userRole
            });
            // Don't close here - wait for the event to show toast first
        } catch (error) {
            console.error('Failed to send invitation:', error);
            setIsSubmitting(false);
            isSubmittingRef.current = false;
            submittedEmailRef.current = '';
        }
    };

    return (
        <>
            <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
                <UserPlus className="w-4 h-4 mr-2" />
                Invite User
            </Button>
            {showUnarchiveTeacherDialog && unarchiveTeacherUserId && (
                <UnarchiveTeacherDialogFromInvite
                    tenantUserId={unarchiveTeacherUserId}
                    tenantUser={submittedEmailRef.current || email}
                    onConfirm={() => {
                        // Convert string back to Guid for the command
                        unarchiveTeacherCommand({
                            $type: 'C243v1_UnarchiveTeacher',
                            C243_UserId: unarchiveTeacherUserId
                        });
                    }}
                    onClose={() => {
                        setShowUnarchiveTeacherDialog(false);
                        setUnarchiveTeacherUserId(null);
                        close();
                    }}
                />
            )}
            {open && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                    <div className="p-5">
                        <div className="flex items-center justify-between mb-5">
                            <h2 className="text-2xl font-bold text-gray-900">Invite User</h2>
                            <button
                                onClick={close}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                disabled={isSubmitting}
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Email Field */}
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                                    Email Address
                                </Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="Enter email address"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="w-full"
                                />
                            </div>

                            {/* First Name Field */}
                            <div className="space-y-2">
                                <Label htmlFor="firstName" className="text-sm font-medium text-gray-700">
                                    First Name
                                </Label>
                                <Input
                                    id="firstName"
                                    type="text"
                                    placeholder="Enter first name"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    className="w-full"
                                />
                            </div>

                            {/* Last Name Field */}
                            <div className="space-y-2">
                                <Label htmlFor="lastName" className="text-sm font-medium text-gray-700">
                                    Last Name
                                </Label>
                                <Input
                                    id="lastName"
                                    type="text"
                                    placeholder="Enter last name"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    className="w-full"
                                />
                            </div>

                            {/* User Role Dropdown */}
                            <div className="space-y-2">
                                <Label htmlFor="userRole" className="text-sm font-medium text-gray-700">
                                    Account Type
                                </Label>
                                <select
                                    id="userRole"
                                    value={userRole}
                                    onChange={(e) => setUserRole(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-action-blue focus:border-action-blue"
                                >
                                    <option value="student">Student</option>
                                    <option value="teacher">Teacher</option>
                                </select>
                                <div className="bg-action-blue/10 p-3 rounded-lg border border-action-blue/30">
                                    <p className="text-sm text-action-blue-dark">
                                        {userRole === 'student' 
                                            ? 'Student accounts have limited access and are designed for learners'
                                            : 'Teacher accounts have full access to create and manage content'
                                        }
                                    </p>
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3 pt-3">
                                <button
                                    type="button"
                                    onClick={close}
                                    disabled={isSubmitting}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={!email.trim() || isSubmitting}
                                    className="px-6 py-2 bg-action-blue hover:bg-action-blue-dark text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed min-w-[140px]"
                                >
                                    {isSubmitting ? "Sending..." : "Send Invite"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            )}
        </>
    );
}
